import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttorney } from '../../context/AttorneyContext';
import { useCreateInternalRequest } from '../../hooks/useMessaging';
import './RequestViaMarkoTMModal.css';

/**
 * Request via MarkoTM Modal
 * Allows external attorneys to submit requests to MarkoTM
 * to contact clients on their behalf.
 */
function RequestViaMarkoTMModal({ isOpen, onClose, applicationId: initialAppId, clientName }) {
  const { t } = useTranslation('attorney');
  const { canCreateInternalRequests, firmName, attorneyName } = useAttorney();
  const createRequest = useCreateInternalRequest();

  const [formData, setFormData] = useState({
    applicationId: initialAppId || '',
    title: '',
    message: '',
    urgency: 'medium',
    items: []
  });

  const [newItem, setNewItem] = useState({ type: 'text', label: '', required: true });

  // Access check
  if (!canCreateInternalRequests) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddItem = () => {
    if (!newItem.label.trim()) return;

    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...newItem, id: `item_${Date.now()}` }]
    }));
    setNewItem({ type: 'text', label: '', required: true });
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.applicationId || !formData.title || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await createRequest.mutateAsync({
        applicationId: formData.applicationId,
        requestData: {
          title: formData.title,
          message: formData.message,
          urgency: formData.urgency,
          items: formData.items
        }
      });

      onClose();
    } catch (error) {
      console.error('Error submitting request:', error);
      alert(`Error submitting request: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content request-via-markotm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('attorney.requestViaMarkoTM.title', 'Request via MarkoTM')}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="info-box">
              <span className="info-icon">💡</span>
              <p>
                {t('attorney.requestViaMarkoTM.info',
                  'Your request will be reviewed by MarkoTM, who will then contact the client on your behalf. You will receive the client\'s response once it\'s forwarded to you.')}
              </p>
            </div>

            {clientName && (
              <div className="request-for">
                <label>{t('attorney.requestViaMarkoTM.for', 'For')}:</label>
                <span>{clientName}</span>
              </div>
            )}

            {!initialAppId && (
              <div className="form-group">
                <label htmlFor="applicationId">
                  {t('attorney.requestViaMarkoTM.application', 'Application ID')} *
                </label>
                <input
                  type="text"
                  id="applicationId"
                  name="applicationId"
                  value={formData.applicationId}
                  onChange={handleChange}
                  placeholder="MTM-2025-00123"
                  required
                />
                <p className="form-hint">
                  {t('attorney.requestViaMarkoTM.applicationHint', 'Enter the MarkoTM application ID')}
                </p>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="title">
                {t('attorney.requestViaMarkoTM.requestTitle', 'Request Title')} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Information needed for trademark filing"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">
                {t('attorney.requestViaMarkoTM.details', 'Request Details')} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                placeholder="Please describe what information you need from the client and why..."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="urgency">
                {t('attorney.requestViaMarkoTM.urgency', 'Urgency')}
              </label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
              >
                <option value="low">{t('attorney.urgency.low', 'Low - No rush')}</option>
                <option value="medium">{t('attorney.urgency.medium', 'Medium - Standard timing')}</option>
                <option value="high">{t('attorney.urgency.high', 'High - Time sensitive')}</option>
                <option value="urgent">{t('attorney.urgency.urgent', 'Urgent - Deadline approaching')}</option>
              </select>
            </div>

            {/* Request Items */}
            <div className="form-section">
              <h3>{t('attorney.requestViaMarkoTM.specificItems', 'Specific Items Needed')}</h3>
              <p className="form-hint">
                {t('attorney.requestViaMarkoTM.itemsHint', 'Add specific items you need from the client')}
              </p>

              {formData.items.length > 0 && (
                <ul className="items-list">
                  {formData.items.map((item, index) => (
                    <li key={index} className="item-row">
                      <span className="item-type">[{item.type}]</span>
                      <span className="item-label">{item.label}</span>
                      {item.required && <span className="item-required">*</span>}
                      <button
                        type="button"
                        className="item-remove"
                        onClick={() => handleRemoveItem(index)}
                      >
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="add-item-form">
                <select
                  value={newItem.type}
                  onChange={(e) => setNewItem(prev => ({ ...prev, type: e.target.value }))}
                >
                  <option value="text">{t('attorney.itemType.text', 'Text Input')}</option>
                  <option value="file_upload">{t('attorney.itemType.file', 'File Upload')}</option>
                  <option value="date">{t('attorney.itemType.date', 'Date')}</option>
                  <option value="select">{t('attorney.itemType.select', 'Selection')}</option>
                </select>
                <input
                  type="text"
                  value={newItem.label}
                  onChange={(e) => setNewItem(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="e.g., 'Current business address'"
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={newItem.required}
                    onChange={(e) => setNewItem(prev => ({ ...prev, required: e.target.checked }))}
                  />
                  Required
                </label>
                <button type="button" className="btn btn-secondary" onClick={handleAddItem}>
                  + Add
                </button>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={createRequest.isPending}
            >
              {createRequest.isPending
                ? t('common.submitting', 'Submitting...')
                : t('attorney.requestViaMarkoTM.submit', 'Submit Request')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RequestViaMarkoTMModal;
